import { v4 as uuid } from 'uuid';

import i18n from '../../../i18n';
import { IAppDispatch } from '../../../redux/core-store';
import { messagesUnwrapPages } from '../../../redux/hooks/answer/utils';
import { ApiTagTypes, ChatEvent, IFailureEvent, MessageStageType, StreamEventType, StreamStatus } from '../../types';

import {
    onAutomaticAssistantMessageUpdater,
    onInputEventUpdater,
    onToolCallEventUpdater,
    onToolResultEventUpdater,
} from './cache-updaters';
import { CONVERSATION_GET_MESSAGES_FUNC } from './consts';
import { messageEndpoints } from './message-endpoints';
import {
    getErrorMessage,
    getLastRunningMessage,
    getMessagePlaceholder,
    isAutomaticAssistantMessage,
    isInputEvent,
    isMessageEvent,
    isToolCallEvent,
    isToolResultEvent,
} from './utils';

export const onMessageEvent = (
    dispatch: IAppDispatch,
    onError: (event: IFailureEvent) => void,
    customer_project_id: string,
    conversation_id: string,
    endpoint: typeof messageEndpoints
) => {
    return (event: ChatEvent, eventType?: StreamEventType) => {
        if (!event) {
            return;
        }
        if ('error' in event) {
            return onError(event);
        }

        // Update the cached data
        dispatch(
            endpoint.util.updateQueryData(
                CONVERSATION_GET_MESSAGES_FUNC,
                { customer_project_id, conversation_id },
                (draft) => {
                    // convert to the `flat` structure
                    const messages = messagesUnwrapPages(draft.pages, false);
                    if (!messages) {
                        return;
                    }

                    const lastErrorMessage = getErrorMessage(messages);
                    if (lastErrorMessage) {
                        lastErrorMessage.is_retryable = false;
                    }

                    if (isAutomaticAssistantMessage(event, eventType)) {
                        return onAutomaticAssistantMessageUpdater(draft, event);
                    }

                    let messageToUpdate = messages.find((m) => m.message_id === event.message_id);

                    const placeholder = getMessagePlaceholder(messages);
                    if (placeholder && !messageToUpdate) {
                        // Remove placeholder from the draft
                        messageToUpdate = placeholder;
                        messageToUpdate.message_id = event.message_id;
                        messageToUpdate.is_placeholder = false;

                        if (event.app_id) {
                            messageToUpdate.app_id = event.app_id;
                            messageToUpdate.app_snapshot = event.app_snapshot;
                        }
                    }

                    if (!messageToUpdate) {
                        return;
                    }

                    if (isToolCallEvent(event, eventType)) {
                        return onToolCallEventUpdater(messageToUpdate, event);
                    }

                    if (isInputEvent(event, eventType)) {
                        return onInputEventUpdater(messageToUpdate, event);
                    }

                    if (isToolResultEvent(event, eventType)) {
                        return onToolResultEventUpdater(messageToUpdate, event);
                    }

                    if (!isMessageEvent(event, eventType)) {
                        return;
                    }

                    messageToUpdate.stage = MessageStageType.ANSWER;
                    const contentBeforeUpdate = messageToUpdate.content;
                    messageToUpdate.content = contentBeforeUpdate + event.content;
                    messageToUpdate.created_at = event.created_at;
                    messageToUpdate.finished_at = event.finished_at;
                    messageToUpdate.updated_at = event.updated_at;
                    messageToUpdate.role = event.role;
                    messageToUpdate.status = event.status;
                    messageToUpdate.footnotes = event.footnotes;
                    messageToUpdate.attributions = event.attributions;

                    if (event.app_id) {
                        messageToUpdate.app_id = event.app_id;
                        messageToUpdate.app_snapshot = event.app_snapshot;
                    }

                    messageToUpdate.created_at ??= new Date();
                }
            )
        );
    };
};

export const onErrorEvent = (
    dispatch: IAppDispatch,
    customer_project_id: string,
    conversation_id: string,
    endpoint: typeof messageEndpoints
) => {
    return (event: IFailureEvent) => {
        dispatch(
            endpoint.util.updateQueryData(
                CONVERSATION_GET_MESSAGES_FUNC,
                { customer_project_id, conversation_id },
                (draft) => {
                    // convert to the `flat` structure
                    const messages = messagesUnwrapPages(draft?.pages, false);
                    if (!messages) {
                        return;
                    }

                    const lastErrorMessage = getErrorMessage(messages);
                    if (lastErrorMessage) {
                        lastErrorMessage.is_retryable = false;
                    }

                    const placeholder = getMessagePlaceholder(messages);
                    const reason = event.reason.toLowerCase() || 'unknown';
                    const content = i18n.t(`error.llm.${reason}`, {
                        ns: 'errors',
                        model: event.debug_info?.model,
                    });
                    if (placeholder) {
                        placeholder.message_id = uuid();
                        placeholder.content = content;
                        placeholder.is_placeholder = false;
                        placeholder.status = StreamStatus.FAILED;
                        placeholder.is_retryable = event.is_retryable;
                        if (event.debug_info) {
                            placeholder.debug_info = event.debug_info;
                        }

                        return;
                    }

                    const runningMessage = getLastRunningMessage(messages);

                    if (runningMessage) {
                        runningMessage.is_placeholder = false;
                        runningMessage.status = StreamStatus.FAILED;
                        runningMessage.content = content;
                        runningMessage.is_retryable = event.is_retryable;
                        if (event.debug_info) {
                            runningMessage.debug_info = event.debug_info;
                        }
                    }
                }
            )
        );
    };
};

export const onAbortEvent = (
    dispatch: IAppDispatch,
    customer_project_id: string,
    conversation_id: string,
    endpoint: typeof messageEndpoints
) => {
    return () => {
        dispatch(
            endpoint.util.updateQueryData(
                CONVERSATION_GET_MESSAGES_FUNC,
                { customer_project_id, conversation_id },
                (draft) => {
                    const firsPage = draft?.pages[0];

                    if (firsPage) {
                        const messageToUpdate = firsPage.messages[firsPage.messages.length - 1];
                        if (messageToUpdate) {
                            messageToUpdate.status = StreamStatus.ABORTED;
                            messageToUpdate.is_placeholder = false;
                        }
                    }
                }
            )
        );
    };
};

export const onCloseEvent = (
    dispatch: IAppDispatch,
    customer_project_id: string,
    conversation_id: string,
    endpoint: typeof messageEndpoints
) => {
    return () => {
        // invalidate the endpoint, to update the cache
        // this will fetch latest list data
        // - generated title if the conversation was created
        // - updated_at for the conversation
        dispatch(
            endpoint.util.updateQueryData(
                CONVERSATION_GET_MESSAGES_FUNC,
                { customer_project_id, conversation_id },
                (draft) => {
                    // in the ideal case it should happen on the first page of draft.pages
                    for (const page of draft?.pages ?? []) {
                        if (page?.messages?.length) {
                            const placeholder = getMessagePlaceholder(page.messages);

                            if (!placeholder) {
                                continue;
                            }

                            page.messages = page.messages.filter((m) => m.message_id !== placeholder.message_id);
                        }
                    }
                }
            )
        );

        dispatch(endpoint.util.invalidateTags([ApiTagTypes.CONVERSATIONS_HISTORY]));
    };
};
